import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
    Plus,
    Eye,
    Edit2,
    Trash2,
    Search,
    Filter,
    X,
    AlertCircle
} from "lucide-react";
import "./MasterStudent.css";
import { fetchStudentList, deleteStudent } from "../../services/studentService";
import { fetchBranchCourseList, fetchBatchList } from "../../services/masterService";
import StudentFormModal from "../../components/StudentFormModal";

const MasterStudent = () => {
    const [students, setStudents] = useState([]);
    const [branches, setBranches] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isViewMode, setIsViewMode] = useState(false);
    const [popup, setPopup] = useState({ show: false, title: "", message: "", type: "success" });
    const [showFilterDropdown, setShowFilterDropdown] = useState(false);
    const [activeFilter, setActiveFilter] = useState({ type: "All", id: null, label: "All Students" });

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 8;

    const [prefillData, setPrefillData] = useState(null);
    const [branchCourseList, setBranchCourseList] = useState([]);
    const [batches, setBatches] = useState([]);
    const location = useLocation();

    useEffect(() => {
        loadData();
        loadMappingData();
    }, []);

    useEffect(() => {
        if (location.state && location.state.prefill) {
            setPrefillData(location.state.prefill);
            setIsEditMode(false);
            setIsViewMode(false);
            setShowAddModal(true);
            window.history.replaceState({}, document.title);
        }
    }, [location]);

    const loadMappingData = async () => {
        try {
            const [bcRes, batchRes] = await Promise.all([
                fetchBranchCourseList(),
                fetchBatchList()
            ]);
            setBranchCourseList(bcRes.data || []);
            setBatches(batchRes.data || []);
        } catch (err) {
            console.error("Failed to load mapping data", err);
        }
    };

    const loadData = async () => {
        try {
            setIsLoading(true);
            const studentRes = await fetchStudentList();
            console.log("studentRes", studentRes);
            setStudents(studentRes.data || []);
        } catch (err) {
            console.error("Load failed", err);
            showPopup("Error", typeof err === "string" && err ? err : "Failed to load students", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const showPopup = (title, message, type = "success") => {
        setPopup({ show: true, title, message, type });
    };

    const formatDateForInput = (dateString) => {
        if (!dateString) return "";
        return dateString.split('T')[0];
    };

    const handleAddClick = async () => {
        setPrefillData(null);
        setSelectedItem(null);
        setIsEditMode(false);
        setIsViewMode(false);
        setShowAddModal(true);
    };

    const handleView = (item) => {
        setSelectedItem(item);
        setPrefillData(null);
        setIsViewMode(true);
        setIsEditMode(false);
        setShowAddModal(true);
    };

    const handleEditClick = (item) => {
        setSelectedItem(item);
        setPrefillData(null);
        setIsEditMode(true);
        setIsViewMode(false);
        setShowAddModal(true);
    };



    const handleDeleteClick = (item) => {
        setSelectedItem(item);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        try {
            setIsLoading(true);
            await deleteStudent(selectedItem.StudentId);
            showPopup("Deleted", "Student has been removed successfully.");
            loadData();
            setShowDeleteModal(false);
        } catch (err) {
            showPopup("Error", typeof err === "string" && err ? err : "Failed to delete student", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const filteredStudents = students.filter(s => {
        const matchesSearch = s.Name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            s.UserName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            s.PhoneNumber?.includes(searchTerm);

        if (activeFilter.type === "All") return matchesSearch;
        if (activeFilter.type === "Batch") return matchesSearch && String(s.BatchId) === String(activeFilter.id);
        if (activeFilter.type === "Course") return matchesSearch && String(s.CourseName) === String(activeFilter.id);
        if (activeFilter.type === "Branch") return matchesSearch && String(s.BranchName) === String(activeFilter.id);

        return matchesSearch;
    });

    const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);
    const paginatedStudents = filteredStudents.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    // Reset to page 1 on search
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);



    return (
        <div className="master-student-container">
            {/* HEADER */}
            <div className="student-header">
                <div className="header-left">
                    <div className="modern-filter-selector" onClick={() => setShowFilterDropdown(!showFilterDropdown)}>
                        <Filter size={18} className="filter-icon" />
                        <div className="current-filter">{activeFilter.label}</div>
                        <X
                            size={14}
                            className={`clear-filter ${activeFilter.type !== "All" ? "visible" : ""}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                setActiveFilter({ type: "All", id: null, label: "All Students" });
                            }}
                        />

                        {showFilterDropdown && (
                            <div className="filter-dropdown-menu">
                                <div
                                    className={`filter-option ${activeFilter.type === "All" ? "active" : ""}`}
                                    onClick={() => { setActiveFilter({ type: "All", id: null, label: "All Students" }); setShowFilterDropdown(false); }}
                                >
                                    All Students
                                </div>

                                <div className="filter-group-label">By Batch</div>
                                {batches.slice(0, 5).map(b => (
                                    <div
                                        key={b.BatchId}
                                        className={`filter-option ${activeFilter.type === "Batch" && activeFilter.id === b.BatchId ? "active" : ""}`}
                                        onClick={() => { setActiveFilter({ type: "Batch", id: b.BatchId, label: `Batch: ${b.BatchName}` }); setShowFilterDropdown(false); }}
                                    >
                                        {b.BatchName}
                                    </div>
                                ))}

                                <div className="filter-group-label">By Course</div>
                                {[...new Set(branchCourseList.flatMap(g => g.CourseList.map(c => c.CourseName)))].slice(0, 5).map(courseName => (
                                    <div
                                        key={courseName}
                                        className={`filter-option ${activeFilter.type === "Course" && activeFilter.id === courseName ? "active" : ""}`}
                                        onClick={() => { setActiveFilter({ type: "Course", id: courseName, label: `Course: ${courseName}` }); setShowFilterDropdown(false); }}
                                    >
                                        {courseName}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="search-box">
                        <Search size={18} className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search students..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
                <div className="header-right">
                    <button className="add-student-btn" onClick={handleAddClick}>
                        <Plus size={20} />
                        <span>Add Student</span>
                    </button>
                </div>
            </div>

            {/* GRID */}
            <div className="student-grid-wrapper">
                {isLoading && (
                    <div className="loader-overlay">
                        <div className="loader-spinner"></div>
                        <p>Fetching Data...</p>
                    </div>
                )}
                <div className="table-scroll-area">
                    <table className="student-table">
                        <thead>
                            <tr>
                                <th>S.No</th>
                                <th>Student Name</th>
                                <th>Parent Name</th>
                                <th>Phone Number</th>
                                <th>Course Name</th>
                                <th>Branch Name</th>
                                <th>Batch Name</th>
                                <th className="text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedStudents.length > 0 ? (
                                paginatedStudents.map((s, index) => (
                                    <tr key={s.StudentId}>
                                        <td><span className="id-badge">{(currentPage - 1) * itemsPerPage + index + 1}</span></td>
                                        <td>
                                            <div className="student-name-cell">
                                                <span>{s.Name}</span>
                                            </div>
                                        </td>
                                        <td>{s.ParentName || "N/A"}</td>
                                        <td>{s.PhoneNumber}</td>
                                        <td>
                                            <span className="course-badge">
                                                {s.CourseName || "N/A"}
                                            </span>
                                        </td>
                                        <td>
                                            <span className="branch-tag">
                                                {s.BranchName || "N/A"}
                                            </span>
                                        </td>
                                        <td>
                                            <span className="batch-badge">
                                                {s.BatchName || "N/A"}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="action-cell">
                                                <button className="action-btn view" onClick={() => handleView(s)} title="View Profile">
                                                    <Eye size={18} />
                                                </button>
                                                <button className="action-btn edit" onClick={() => handleEditClick(s)} title="Edit Student">
                                                    <Edit2 size={18} />
                                                </button>
                                                <button className="action-btn delete" onClick={() => handleDeleteClick(s)} title="Delete">
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="8" className="text-center" style={{ padding: '40px', color: '#94a3b8' }}>
                                        {isLoading ? "Loading students..." : "No students found"}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* PAGINATION CONTROLS */}
                {totalPages > 1 && (
                    <div className="pagination-container">
                        <div className="pagination-info">
                            Showing <span>{(currentPage - 1) * itemsPerPage + 1}</span> to <span>{Math.min(currentPage * itemsPerPage, filteredStudents.length)}</span> of <span>{filteredStudents.length}</span> students
                        </div>
                        <div className="pagination-btns">
                            <button
                                className="pag-btn"
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                            >
                                Previous
                            </button>
                            {[...Array(totalPages)].map((_, i) => (
                                <button
                                    key={i + 1}
                                    className={`pag-num ${currentPage === i + 1 ? 'active' : ''}`}
                                    onClick={() => setCurrentPage(i + 1)}
                                >
                                    {i + 1}
                                </button>
                            ))}
                            <button
                                className="pag-btn"
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>



            <StudentFormModal
                isOpen={showAddModal}
                onClose={() => setShowAddModal(false)}
                onSuccess={(msg) => {
                    showPopup("Success", msg);
                    setShowAddModal(false);
                    loadData();
                }}
                isEditMode={isEditMode}
                isViewMode={isViewMode}
                prefillData={prefillData}
                selectedItem={selectedItem}
                branchCourseList={branchCourseList}
                batches={batches}
            />

            {/* DELETE MODAL */}
            {showDeleteModal && (
                <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
                    <div className="modal-card mini" onClick={(e) => e.stopPropagation()}>
                        <div className="delete-confirm-body">
                            <AlertCircle size={48} color="#ef4444" />
                            <h3>Confirm Delete</h3>
                            <p>Are you sure you want to delete student <b>{selectedItem?.Name}</b>? This action is permanent.</p>
                            <div className="confirm-actions">
                                <button className="cancel-btn" onClick={() => setShowDeleteModal(false)}>Keep Student</button>
                                <button className="delete-confirm-btn" onClick={confirmDelete}>Delete Now</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* POPUP */}
            {popup.show && (
                <div className="popup-overlay" onClick={() => setPopup({ ...popup, show: false })}>
                    <div className="popup-card" onClick={(e) => e.stopPropagation()}>
                        <div className={`popup-icon ${popup.type}`}>
                            {popup.type === "success" ? "✓" : "!"}
                        </div>
                        <h2>{popup.title}</h2>
                        <p>{popup.message}</p>
                        <button className="popup-btn" onClick={() => setPopup({ ...popup, show: false })}>Got it</button>
                    </div>
                </div>
            )}
        </div>
    );
};

const ModernDetailItem = ({ icon, label, value, highlight, isBadge }) => (
    <div className={`modern-detail-item ${highlight ? `highlight-${highlight}` : ""}`}>
        <div className="detail-icon-wrap">{icon}</div>
        <div className="detail-content">
            <span className="detail-label">{label}</span>
            <span className={`detail-value ${isBadge ? "badge-value" : ""}`}>{value || "N/A"}</span>
        </div>
    </div>
);

const FormInput = ({ label, name, value, isAlpha, onChange, type = "text", icon, error, isNumeric, maxLength, setErrors, readOnly }) => (
    <div className={`form-input-group ${error ? "has-error" : ""}`}>
        <label>{label}</label>
        <div className={`input-with-icon ${error ? "error-border" : ""} ${readOnly ? "readonly-input-wrap" : ""}`}>
            {icon && <span className="input-icon">{icon}</span>}
            <input
                type={type}
                value={value}
                onChange={(e) => {
                    if (readOnly) return;
                    let val = e.target.value;
                    if (isNumeric) {
                        val = val.replace(/\D/g, "");
                    }

                    if (isAlpha) {
                        val = val.replace(/[0-9]/g, "");
                    }
                    if (maxLength && val.length > maxLength) {
                        val = val.slice(0, maxLength);
                    }
                    onChange(prev => ({ ...prev, [name]: val }));
                    if (setErrors) {
                        setErrors(prev => ({ ...prev, [name]: "" }));
                    }
                }}
                placeholder={readOnly ? "" : `Enter ${label.toLowerCase()}`}
                readOnly={readOnly}
                className={readOnly ? "readonly-input" : ""}
            />
        </div>
        {error && <span className="error-message-inline">{error}</span>}
    </div>
);

export default MasterStudent;
